import PriceStatistic from "common/Statistic/PriceStatistic";
import Statistic from "common/Statistic/Statistic";
import Space from "antd/es/space";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { ICON_TYPES } from "@shared/common/constants";
import {
  CreateOrderGeneralInfo,
  LanguageNameSpaces,
  PrecisionType,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import styled, { useTheme } from "styled-components";
import StatStrip from "common/NewLook/StatStrip.tsx";
import StatAmount from "common/NewLook/StatAmount.tsx";
import { getPairFromSymbol } from "@shared/utils/misc";

interface GeneralInfoBoxesProps {
  symbol?: string;
  pair?: string;
  availableCost?: number;
  isEntry?: boolean;
  generalInfo?: CreateOrderGeneralInfo;
}

const GeneralInfoBoxes = ({
  symbol,
  pair,
  availableCost,
  isEntry,
  generalInfo,
}: GeneralInfoBoxesProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { t: tTrades } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const costPrecisionType = isEntry
    ? PrecisionType.AMOUNT
    : PrecisionType.QUANTITY;
  // The price is quoted in the pair's quote coin, whichever side is available.
  const priceCoin = pair?.includes("/") ? getPairFromSymbol(pair) : symbol;

  const { isNewLook } = useTheme();

  if (isNewLook) {
    return (
      <CenteredStatStrip
        variant="flat"
        items={[
          {
            key: "available",
            label: t("create_order.available_amount"),
            value: (
              <StatAmount
                text={availableCost}
                coin={symbol}
                precisionConfig={generalInfo?.precision}
                precisionType={costPrecisionType}
              />
            ),
          },
          {
            key: "price",
            label: tTrades("statistics.current_price"),
            value: (
              <StatAmount
                text={generalInfo?.data.current_price}
                coin={priceCoin}
                precisionConfig={generalInfo?.precision}
                precisionType={PrecisionType.PRICE}
              />
            ),
          },
        ]}
      />
    );
  }

  return (
    <Space size={20} style={{ width: "100%", justifyContent: "center" }}>
      <Statistic
        title={t("create_order.available_amount")}
        value={
          <TextWithPrecisionAndIcon
            text={availableCost}
            precisionConfig={generalInfo?.precision}
            precisionType={costPrecisionType}
            precisionCoin={symbol}
            iconType={ICON_TYPES.COIN}
            coinOrExchangeName={symbol}
          />
        }
      />
      <PriceStatistic
        currentPrice={generalInfo?.data.current_price}
        coin={priceCoin}
        precisionConfig={generalInfo?.precision}
      />
    </Space>
  );
};

export default GeneralInfoBoxes;

// Centered like the pair line above and the amount input below.
const CenteredStatStrip = styled(StatStrip)`
  justify-content: center;
  justify-items: center;
  text-align: center;
  > * {
    align-items: center;
  }
`;
